import MediaInfo from "mediainfo.js";
import { VIDEO_TYPE_LIMIT } from "@/constants";
import { withPublicPath, IS_PROD } from ".";
// @ts-ignore
import { UploadFile, UploadStatus } from "ft-utils/dist/fotor-utils.es.js"


export const VideoError = {
  commonError: "video_other_error",
  formatError: "video_format_error",
  sizeError: "video_size_error",
  frameRateError: "video_frame_rate_error",
  durationError: "video_duration_error",
  dimensionsError: "video_dimensions_error",
}

/**
 * 解析视频，判读格式、时长、大小、画幅、帧率
 * 视频格式：.mp4、.m4v、.webm、.mov、.avi
 * 视频时长最大：60s
 * 视频大小最大：1024 MB
 * 视频画幅最大边：1920
 * 视频帧率最大：30 
 * @param file 
 * @returns 
 */
export async function checkVideo(file: File, skipCheck?: boolean): Promise<{
  width: number;
  height: number;
  duration: number;
  frameRate: number;
  file: File;
  url: string;
  thumb: string;
}> {
  const MAX_SIZE = 1024 * 1024 * 1024; // B
  const MIN_SIZE = 1; // B
  const TYPE_LIMIT = VIDEO_TYPE_LIMIT;
  const MAX_DURATION = 60; // s
  const MIN_DURATION = 1; // s
  const MAX_FRAME_RATE = 30; // 帧率限制, 后端会压缩
  const MIN_FRAME_RATE = 1;
  const MAX_WIDTH = 1920; // pixel
  const MIN_WIDTH = 1; // pixel
  const MAX_HEIGHT = 1920; // pixel
  const MIN_HEIGHT = 1; // pixel

  // 检查大小
  if (!skipCheck && (file.size > MAX_SIZE || file.size < MIN_SIZE)) {
    throw new Error(VideoError.sizeError);
  }

  // 检查格式
  if (!skipCheck && !TYPE_LIMIT.includes(file.type)) {
    throw new Error(VideoError.formatError);
  }

  const videoInfo = await getVideoInfo(file);

  if (
    !videoInfo ||
    typeof videoInfo.Duration !== "number" ||
    typeof videoInfo.FrameRate !== "number" ||
    typeof videoInfo.Width !== "number" ||
    typeof videoInfo.Height !== "number"
  ) {
    throw new Error(VideoError.commonError);
  }


  // 检查时长
  if (!skipCheck && videoInfo.Duration > MAX_DURATION) {
    throw new Error(VideoError.durationError);
  }

  // 检查帧率
  if (
    !skipCheck &&
    (videoInfo.FrameRate > MAX_FRAME_RATE ||
      videoInfo.FrameRate < MIN_FRAME_RATE)
  ) {
    throw new Error(VideoError.frameRateError);
  }
  const realWidth = Math.max(videoInfo.Width, videoInfo.Height);
  const realHeight = Math.min(videoInfo.Width, videoInfo.Height);

  if (!skipCheck && (realWidth > MAX_WIDTH || realHeight > MAX_HEIGHT)) {
    throw new Error(VideoError.dimensionsError);
  }

  if (!skipCheck && (realWidth < MIN_WIDTH || realHeight < MIN_HEIGHT)) {
    throw new Error(VideoError.dimensionsError);
  }

  const thumb: any = await getVideoFirstFrame(file).catch((err) => {
    console.error("getVideoFirstFrame error", err);
  });

  // // 测试代码
  // const img = new Image();
  // img.src = thumb;
  // document.body.appendChild(img);

  return {
    width: videoInfo.Width,
    height: videoInfo.Height,
    duration: videoInfo.Duration,
    frameRate: videoInfo.FrameRate,
    file: file,
    url: URL.createObjectURL(file),
    thumb,
  }
}

let mediaInfoInstance: any = null;
export const initMediaInfo = () => {
  return new Promise((resolve, reject) => {
    if (mediaInfoInstance) {
      resolve(mediaInfoInstance);
      return;
    }
    MediaInfo({
      format: 'object',
      locateFile: (path) => {
        return withPublicPath(`mediainfo.js/${path}`); // 自定义路径
      },
    })
      .then((instance: any) => {
        mediaInfoInstance = instance;
        resolve(instance);
      })
      .catch((err: any) => {
        console.error("Error initializing MediaInfo:", err);
        reject(err);
      });
  })
};

export const downloadBlob = (
  url: string,
  onProgress?: (value: number) => void,
  isSafari?: boolean,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = isSafari ? "arraybuffer" : "blob";
      xhr.onload = function () {
        if (this.status === 200) {
          onProgress?.(100);
          resolve(
            isSafari
              ? new Blob([this.response], { type: "video/mp4" })
              : this.response,
          );
        }
      };
      xhr.onloadend = function () {
        if (xhr.status == 404) reject("404");
      };
      xhr.onerror = function (err) {
        reject(err);
      };
      xhr.onprogress = function (event) {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress?.(progress);
        } else {
          onProgress?.(0);
        }
      };
      xhr.send();
    } catch (error) {
      reject(error);
    }
  });
};

// progress: 0 ~ 100
export const preload = (
  url: string,
  onProgress?: (value: number) => void,
  isSafari?: boolean,
): Promise<string> => {
  return downloadBlob(url, onProgress, isSafari).then((blob) =>
    URL.createObjectURL(blob),
  );
};

export const downloadVideoByUrl = (url: string): Promise<File> => {
  return fetch(url, { mode: 'cors' }) // 确保启用 CORS
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.statusText}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const urlParts = url.split('/');
      const fileName = decodeURIComponent(urlParts[urlParts.length - 1] || 'video.mp4');

      // Safari 有时会忽略 blob.type，手动指定更稳妥
      const fixedBlob = new Blob([blob], { type: 'video/mp4' });

      return new File([fixedBlob], fileName, { type: 'video/mp4' });
    });
};


export const isNative = (videoSrc: string) => videoSrc.startsWith("blob:");

export const getVideoInfo = (file: File) => {
  return new Promise<any>(async (resolve, reject) => {
    const mediaInfo = await initMediaInfo();

    const getSize = () => file.size;
    const readChunk = (chunkSize: number, offset: number) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event: any) => {
          if (event.target.error) {
            reject(event.target.error);
          }
          resolve(new Uint8Array(event.target.result));
        };
        reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
      });
    const callback = (instance: any) => {
      // instance.close();
      // instance.mediainfoModule = null;
      // instance.mediainfoModuleInstance = null;
      // window.mediaInfo = null;
    };

    const doMetiaoInfo = (instance: any) => {
      instance
        .analyzeData(getSize, readChunk)
        .then((result: any) => {
          const videoInfo = result?.media?.track?.find(
            (item: any) => item["@type"] === "Video",
          );
          if (videoInfo) {
            callback(instance);
            resolve(videoInfo);
          } else {
            callback(instance);
            reject();
          }
        })
        .catch((err: any) => {
          callback(instance);
          reject(err);
        });
    };

    // if (!mediaInfo) {
    //   window.MediaInfo({ format: "object" }, (mediaInfo: any) => {
    //     window.mediaInfo = mediaInfo;
    //     doMetiaoInfo(mediaInfo);
    //   });
    //   return;
    // }

    doMetiaoInfo(mediaInfo);
  });
};

export const getVideoFirstFrame = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      reject("can not get canvas context");
      return;
    }

    const videoURL = URL.createObjectURL(file);
    video.src = videoURL;
    // 不设置 crossOrigin（本地 File 会失败）
    video.crossOrigin = "anonymous";
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;

    const cleanup = () => {
      URL.revokeObjectURL(videoURL);
      document.body.removeChild(video);
    };

    video.addEventListener("loadeddata", async () => {
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // iOS Safari: 必须 play 一次，再 pause，确保绘图可用
        await video.play();
        video.pause();

        // 等待渲染帧同步
        requestAnimationFrame(() => {
          try {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              cleanup();
              if (blob) {
                resolve(URL.createObjectURL(blob));
              } else {
                reject("canvas toBlob failed");
              }
            });
          } catch (e) {
            reject("drawImage error");
          }
        });
      } catch (e) {
        cleanup();
        reject("video playback error");
      }
    });

    video.addEventListener("error", () => {
      cleanup();
      reject("video load error");
    });

    // 插入 DOM 是必要的（iOS 会优化未附加元素）
    video.style.position = "absolute";
    video.style.left = "-9999px";
    document.body.appendChild(video);
    video.load(); // 手动触发 load
  });
};





export const uploadVideo = async (file: File) => {

  const uploadInstance = new UploadFile({
    env: IS_PROD ? "prod" : "test",
    // 切片大小
    sliceSize: 1024 * 1024 * 5,
    customUploadParams: {
      type: "aifaceswapvideo",
    }
  })

  // 开始上传文件
  const res = await uploadInstance.upload(file);

  if (res) {
    const { originUrl, uploadKey } = res
    console.log("upload Success!", uploadKey, originUrl)

    return originUrl
  }
}